import React from 'react';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText,
  Button,
  HeaderText,
  Stack,
} from '../../../../ui';

import {
  CloseIcon,
} from '../../../../ui/Icons';

import { APP } from '../../../../../config';
import Carousel from '../../../../Carousel';
import analytics from '../../../../../modules/analytics';
import { useMount, useIsMobile } from '../../../../../hooks';
import Image from '../../../../shared/Image';
import PortalOverlay, { PortalOverlayModal } from '../../../../ui/PortalOverlay';
import { PROFILE_EDIT_ROUTE } from '../../../../../route-names';

const SLIDE_IMAGE_ONE = require('../../../../../../images/client-experience-carousel-slide-1.png');
const SLIDE_IMAGE_TWO = require('../../../../../../images/client-experience-carousel-slide-2.png');
const SLIDE_IMAGE_THREE = require('../../../../../../images/client-experience-carousel-slide-3.png');
const SLIDE_IMAGE_FOUR = require('../../../../../../images/client-experience-carousel-slide-4.png');

type ClientExperienceModalProps = {
  providerId: number,
  onClose: () => void,
  openBulkImportModal: () => void,
  openAddClientModal: () => void,
};

const ClientExperienceModal = ({
  providerId,
  onClose,
  openBulkImportModal,
  openAddClientModal,
}: ClientExperienceModalProps) => {
  const isApp = !!APP;

  const slidesData = [
    {
      image: SLIDE_IMAGE_ONE,
      header: 'Your new professional profile',
      subHeader: 'Clients can see all your services and photos of your work',
    },
    {
      image: SLIDE_IMAGE_TWO,
      header: 'Your availability',
      subHeader: 'Clients only see the times you are available for the service they requested.',
    },
    {
      image: SLIDE_IMAGE_THREE,
      header: 'Simple and easy payments',
      subHeader: 'Check out clients from your phone and get paid if they don’t show up.',
    },
    {
      image: SLIDE_IMAGE_FOUR,
      header: 'Appointment Details',
      subHeader: 'Clients are notified before their appointments and get reminders about their appointments.',
    },
  ];

  const isMobile = useIsMobile();

  /** onChange callback to handle tracking from swiping of slides */
  const onChange = (slide, index) => {
    switch (index) {
      case (0):
        analytics.track('pro_whatclientssee_profile_view');
        break;
      case (1):
        analytics.track('pro_whatclientssee_availability_view');
        break;
      case (2):
        analytics.track('pro_whatclientssee_payments_view');
        break;
      case (3):
        analytics.track('pro_whatclientsee_apptdetails_view');
        break;
      default:
        break;
    }
  };

  /** handles the add clients click */
  const handleAddClientsClick = () => {
    onClose();
    if (isApp) {
      openBulkImportModal();
    } else {
      openAddClientModal();
    }
  };

  /** handles hiding of the client experience modal */
  const handleCloseModal = () => {
    onClose();
  };

  useMount(() => {
    analytics.track('pro_whatclientssee_view');
    analytics.track('pro_whatclientssee_profile_view'); // first slide of carousel
  });

  return (
    <PortalOverlay
      name="client-experience-modal"
    >
      <PortalOverlayModal position="fullScreen">
        <Box
          padding={16}
          overflow="auto"
          style={{
            height: '100%',
            backgroundColor: colors.itsOff,
          }}
        >
          <Box
            flexDirection="row-reverse"
            paddingBottom={isMobile ? 24 : 8}
          >
            <Button
              appearance="none"
              size="medium"
              onClick={handleCloseModal}
              testID="client-experience-modal-dismiss-button"
              clickTracking="pro_whatclientssee_dismiss"
            >
              <CloseIcon
                color={colors.ash}
                height={20}
                width={20}
              />
            </Button>
          </Box>
          <Box
            paddingX={isMobile ? 0 : 36}
          >
            <HeaderText
              level={isMobile ? 1 : 2}
              textAlign="center"
            >
              What do my clients see when they use StyleSeat?
            </HeaderText>
            <Box
              borderRadius={8}
              minHeight={isMobile ? 394 : 360}
              style={{
                backgroundColor: 'rgba(225, 9, 132, -0.95)',
                marginTop: isMobile ? '32px' : '24px',
                marginLeft: isMobile ? '24px' : '44px',
                marginRight: isMobile ? '24px' : '44px',
              }}
              testID="client-experience-modal-carousel-container"
            >
              <Carousel
                autoPlayInterval={3000}
                onChangeSlide={onChange}
                paginationColor={colors.punch}
              >
                {slidesData.map((slide, slideIndex) => {
                  const defaultSlideHeight = isMobile ? 230 : 206;
                  const lastSlideHeight = isMobile ? 216 : 192;
                  const imageContainerHeight = slideIndex === 3
                    ? lastSlideHeight
                    : defaultSlideHeight;
                  return (
                    <Stack
                      paddingTop={isMobile ? 32 : 24}
                      space={12}
                      paddingX={28}
                      color={colors.whiteWash}
                      borderRadius={8}
                      key={slide.header}
                      style={{
                        shadowOffset: { width: 0, height: 2 },
                        shadowRadius: 4,
                        shadowColor: 'rgba(40, 41, 41, 0.2)',
                        margin: 4,
                      }}
                    >
                      <HeaderText
                        level={3}
                        textAlign="center"
                      >
                        {slide.header}
                      </HeaderText>
                      <BodyText
                        appearance="small"
                        color={colors.ash}
                        textAlign="center"
                      >
                        {slide.subHeader}
                      </BodyText>
                      <Box
                        overflow="hidden"
                        paddingTop={16}
                        alignItems="center"
                        style={{
                          height: imageContainerHeight,
                        }}
                      >
                        <Image
                          source={slide.image}
                          style={{
                            height: 322,
                            width: 175,
                            position: 'relative',
                            top: -12,
                          }}
                        />
                      </Box>
                    </Stack>
                  );
                })}
              </Carousel>
            </Box>

            <Inline
              paddingTop={isMobile ? 12 : 0}
              paddingX={8}
              space={8}
              justifyContent="center"
            >
              <Button
                clickTracking="pro_whatclientssee_editprofile_click"
                testID="pro-whatclientssee-edit-profile-button"
                link={PROFILE_EDIT_ROUTE}
                linkParams={{
                  providerId,
                }}
                style={{
                  width: '100%',
                }}
              >
                Edit Profile
              </Button>

              <Button
                onClick={handleAddClientsClick}
                clickTracking="pro_whatclientssee_add_clients_click"
                testID="pro-whatclientssee-add-clients-button"
                style={{
                  width: '100%',
                }}
              >
                Add Clients
              </Button>
            </Inline>

          </Box>
          <Box
            paddingTop={isMobile ? 12 : 4}
          >
            <Button
              appearance="link"
              onClick={handleCloseModal}
              style={{
                color: colors.metal,
                borderBottomColor: colors.metal,
              }}
              clickTracking="pro_whatclientssee_close_click"
              testID="client-experience-modal-close-button"
            >
              Close
            </Button>
          </Box>
        </Box>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};

export default ClientExperienceModal;
